import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Download,
  ArrowLeft,
  Building2,
  Phone,
  Mail,
  Globe,
  Calendar,
  CheckCircle2,
  Zap,
  Shield,
  DollarSign,
  Clock
} from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "/logos/askisaac-b-monogram.svg";
const ASKISAAC_LOGO = "/logos/askisaac-b-monogram.svg";

export default function HospitalExecutiveLetterPage() {
  const [recipientName, setRecipientName] = useState("[Executive Name]");
  const [recipientTitle, setRecipientTitle] = useState("VP of Operations");
  const [hospitalName, setHospitalName] = useState("[Hospital Name]");

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .print-content, .print-content * { visibility: visible; }
          .print-content { 
            position: absolute; 
            left: 0; 
            top: 0; 
            width: 100%;
            padding: 0;
            margin: 0;
          }
          .no-print { display: none !important; }
          @page { 
            size: letter portrait; 
            margin: 0.75in; 
          }
          .print-content {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>

      {/* Navigation Header - No Print */}
      <div className="no-print bg-white border-b border-slate-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back to Home</span>
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/sales-one-pager" className="text-sm text-blue-600 hover:underline">
                View One-Pager
              </Link>
              <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700">
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Personalization Panel - No Print */}
      <div className="no-print bg-slate-50 border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <p className="text-sm text-slate-600 mb-3 font-medium">Personalize this letter (optional):</p>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-slate-500">Recipient Name</label>
              <input
                type="text"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                placeholder="[Executive Name]"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500">Title</label>
              <input
                type="text"
                value={recipientTitle}
                onChange={(e) => setRecipientTitle(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                placeholder="VP of Operations"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500">Hospital Name</label>
              <input
                type="text"
                value={hospitalName}
                onChange={(e) => setHospitalName(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                placeholder="[Hospital Name]"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Printable Letter Content */}
      <div className="print-content bg-white min-h-screen">
        <div className="max-w-3xl mx-auto p-8 md:p-12">
          
          {/* Letterhead */}
          <div className="flex items-center justify-between mb-8 pb-6 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-slate-900 p-1.5 flex items-center justify-center">
                <img src={ASKISAAC_LOGO} alt="AskIsaac" className="w-full h-full object-contain" />
              </div>
              <div>
                <p className="font-bold text-slate-900">Sterile Mate LLC</p>
                <p className="text-cyan-600 text-sm font-medium">AskIsaac.io</p>
              </div>
            </div>
            <div className="text-right text-sm text-slate-500">
              <p>800.988.3892</p>
              <p>info@sterilemate.com</p>
              <p>www.sterilemate.com</p>
            </div>
          </div>

          {/* Date */}
          <p className="text-slate-500 text-sm mb-8">
            {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>

          {/* Subject Line */}
          <div className="bg-slate-900 text-white rounded-xl p-5 mb-8">
            <p className="text-sm text-slate-400 mb-1">Subject:</p>
            <h1 className="text-xl font-bold">
              Beyond the &ldquo;GPO Excuse&rdquo;: Bridging the $500K Reliability Gap with AskIsaac.io
            </h1>
          </div>

          {/* Salutation */}
          <p className="text-slate-800 mb-6">
            Dear {recipientName},
          </p>

          {/* Opening */}
          <p className="text-slate-700 leading-relaxed mb-6">
            In the healthcare supply chain, the phrase <strong>&ldquo;We are under a GPO contract&rdquo;</strong> has 
            become the standard explanation for canceled surgeries and equipment downtime. While Group Purchasing 
            Organizations provide necessary volume savings, they often create a &ldquo;Push-Button&rdquo; trap: when 
            the primary distributor fails to deliver due to backorders or allotments, facility teams are often left 
            without a compliant path to a solution.
          </p>

          <p className="text-slate-700 leading-relaxed mb-6">
            At <strong>Sterile Mate LLC</strong>, we have spent 6 years in the trenches of the Texas Medical Center 
            witnessing the cost of this gap — an average of <strong className="text-red-600">$500,000 in annual 
            downtime leaks per facility</strong> and millions wasted on over-specified hardware.
          </p>

          <p className="text-slate-700 leading-relaxed mb-8">
            We built <strong className="text-cyan-600">AskIsaac.io</strong> to be the &ldquo;Safety Valve&rdquo; for 
            the modern hospital.
          </p>

          {/* Solution Section */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 mb-8">
            <h2 className="text-lg font-bold text-emerald-900 mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-emerald-600" />
              The Solution: A Marketplace for Resilience
            </h2>
            <p className="text-slate-700 mb-4">
              AskIsaac.io is a <strong>Global Orchestration Exchange</strong> that connects your facility directly 
              to the pre-vetted, credentialed local talent that is often &ldquo;hidden&rdquo; right in your own backyard.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Clock className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">The &ldquo;QuickBid&rdquo; Engine</p>
                  <p className="text-slate-600 text-sm">
                    When your GPO provider fails, don&apos;t wait for a backorder date. Our platform allows your team 
                    to bypass the manual 3-quote bottleneck by instantly identifying verified local partners who have 
                    the parts and the &ldquo;healthcare-ready&rdquo; credentials to act today.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Shield className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Automated Compliance (AAMI ST108)</p>
                  <p className="text-slate-600 text-sm">
                    We remove the administrative burden of going &ldquo;off-contract.&rdquo; AskIsaac manages the 
                    supplier vetting and digital validation, ensuring that every transaction meets the highest 
                    regulatory standards.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <DollarSign className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Cost Rectification</p>
                  <p className="text-slate-600 text-sm">
                    We use data-driven validation to stop the &ldquo;over-speccing&rdquo; of infrastructure. We&apos;ve 
                    proven that hospitals can save over <strong>$1M on single-department water systems</strong> by using 
                    software-backed validation instead of hardware-heavy legacy quotes.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* The Ask */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
            <h2 className="text-lg font-bold text-blue-900 mb-3">The Ask</h2>
            <p className="text-slate-700 mb-4">
              We aren&apos;t asking you to replace your GPO. We are asking you to empower your team with a 
              <strong className="text-blue-600"> digital backup generator</strong>. When the &ldquo;Big Box&rdquo; 
              power goes out, AskIsaac kicks in to keep your ORs running.
            </p>
            <p className="text-slate-700">
              I would like to invite you to a brief <strong>15-minute demonstration</strong> of how we are currently 
              bridging this gap for major institutions in the TMC. You can view our engine at{" "}
              <a href="https://www.sterilemate.com" className="text-blue-600 font-semibold hover:underline">
                www.sterilemate.com
              </a>.
            </p>
          </div>

          {/* Availability */}
          <p className="text-slate-700 mb-8">
            <strong>Are you available next Tuesday or Wednesday for a brief discussion?</strong>
          </p>

          {/* Signature */}
          <div className="mt-8">
            <p className="text-slate-700 mb-4">Best regards,</p>
            <div className="flex items-start gap-4">
              <img src={LOGO_URL} alt="Sterile Mate" className="w-16 h-auto" />
              <div>
                <p className="font-bold text-slate-900">George Broughton III</p>
                <p className="text-slate-600">CEO, Sterile Mate LLC / AskIsaac.io</p>
                <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                  <span className="flex items-center gap-1">
                    <Phone className="w-3 h-3" /> 800.988.3892
                  </span>
                  <span className="flex items-center gap-1">
                    <Mail className="w-3 h-3" /> info@sterilemate.com
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-12 pt-6 border-t border-slate-200 text-center">
            <p className="text-xs text-slate-400">
              © {new Date().getFullYear()} Sterile Mate LLC | www.sterilemate.com | 800.988.3892
            </p>
          </div>

        </div>
      </div>
    </>
  );
}
